/**
 * Showing a player: their name, their badges, and the popout card.
 *
 * The full profile is reachable only through this card - clicking a name
 * anywhere in the app opens the popout, and the card is the way on to the
 * page. That keeps one route into a profile rather than five.
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { EmojiText } from '@/components/EmojiText';
import { copyText } from '@/lib/clipboard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { endpoints, serverUrl } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { FONTS } from '@/lib/fonts';
import { useLayer } from '@/lib/layers';
import { go } from '@/lib/hash';
import { openProfile } from '@/components/ProfilePanel';
import { useSession } from '@/stores/session';
import { useLocal } from '@/stores/local';
import { Avatar } from './Avatar';
import { BadgeMark } from './BadgeMark';
import { Tip } from './HoverCard';
import { useUserMenu } from './userMenu';
import { FramedAvatar } from './FramedAvatar';
import { Icon } from '@/icons/Icon';
import { Tag, useToast } from './ui';
import * as Icons from '@/icons';
import { gameIconArt } from '@/lib/gameArt';
import { CrewTag } from './CrewTag';
import { api } from '@/lib/api';

/* -------------------------------------------------------------- name */

/**
 * A player's name, with the VIP/Premium colour treatment when they have one.
 * A single colour gets an auto-brightened partner so the gradient has
 * somewhere to travel - otherwise "animated" looks completely static.
 */
export function UserName({
  user, className, big, onClick, profile
}: {
  user: Pick<PublicUser, 'id' | 'displayName' | 'username' | 'nameColor' | 'nameColor2' | 'nameAnimated' | 'nameFont'>;
  className?: string;
  big?: boolean;
  /**
   * Whether clicking the name opens the compact profile. On by default - a
   * player's name should always lead to the player - and turned off only
   * where the name is already inside that profile.
   */
  profile?: boolean;
  /*
   * Makes the name itself clickable - almost always to open the profile.
   *
   * A span rather than a button on purpose: names sit inside rows that are
   * already buttons, and a button inside a button is invalid and behaves
   * unpredictably. The handler stops propagation at the call site so the row
   * underneath does not also fire.
   */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  // Your own nickname for them wins over the name they chose, everywhere.
  const nick = useLocal((s) => s.nicknames[user.id]);
  const name = nick || user.displayName;

  /*
   * The shared profile behaviour: a name opens that player's compact profile,
   * by their id, unless the caller gave it another job. Only when the object
   * is a whole player - a stub with just a name cannot fill a profile card.
   */
  const whole = 'level' in user && 'avatar' in user;
  const handle = onClick ?? (profile !== false && whole
    ? (e: React.MouseEvent<HTMLElement>) => {
        /* Inside something that already does a job when pressed - a person
           you pick in Forward, a conversation row, a button - the name belongs
           to that thing, so the press goes through to it. */
        if (e.currentTarget.parentElement?.closest('button, a, [role="button"], [role="option"], [role="menuitem"], [role="checkbox"], label, [data-no-profile]')) return;
        e.stopPropagation();
        openUserCard(user as PublicUser, e.currentTarget);
      }
    : undefined);
  const keys = handle
    ? {
        tabIndex: 0,
        'aria-label': 'Show ' + name + "'s profile",
        onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          (e.currentTarget as HTMLElement).click();
        }
      }
    : {};

  /*
   * The typeface, when they chose one.
   *
   * Looked up rather than taken as given: the value is an id from a fixed
   * list, and anything not on the list draws in the body font - which is
   * what somebody who has never touched this sees anyway.
   *
   * Both branches below pad the box and pull the padding back off again.
   *
   * truncate means overflow:hidden, and the content box is sized from the
   * line box - which for Montserrat, Sora and Nunito at 18px is about a
   * pixel shorter than the glyphs actually need. The descenders were being
   * sliced, which reads as the name being cut rather than the box being
   * short, and it is worse on a gradient name because background-clip:text
   * paints exactly the glyph shapes and nothing else.
   *
   * Padding moves where the clip happens; the negative margin takes the
   * height back, so nothing anywhere else in the app shifts by a pixel.
   * Raising the line-height instead would have done the same job and made
   * every chat row taller, which is a worse trade in a list of two hundred.
   */
  const def = user.nameFont ? FONTS.find((x) => x.id === user.nameFont) : undefined;
  const face = def?.stack;
  /*
   * Single-weight faces are pinned to 400.
   *
   * Every name in the app is drawn bold or extrabold, and eleven of the
   * twelve name faces ship one weight only. Asking those for 700 makes the
   * engine fake it by smearing each glyph sideways - which on Silkscreen
   * reads as a rendering fault and on UnifrakturMaguntia as a blot. The
   * inline weight beats the class, so the caller does not have to know.
   */
  const faceStyle: CSSProperties | undefined = face
    ? { fontFamily: face, ...(def?.single ? { fontWeight: 400 } : {}) }
    : undefined;

  const a = user.nameColor;
  if (!a) {
    return (
      <span
        {...keys}
        onClick={handle}
        style={faceStyle}
        role={handle ? 'button' : undefined}
        className={cn('min-w-0 truncate py-[.14em] -my-[.14em]', handle && 'cursor-pointer rounded-[4px] outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]', big && 'text-[22px] font-extrabold', className)}
      >
        {name}
      </span>
    );
  }

  const b = user.nameColor2 ?? lighten(a);
  /*
   * A gradient that tiles, so the loop has no seam.
   *
   * This used to be sized at 220% of the name and slid to
   * background-position: 220%, which is a percentage of (box - image) and so
   * depends on how long the name is. The travel almost never matched the
   * tile, so every four and a half seconds the colours jumped back to the
   * start - the flicker.
   *
   * A tile measured in pixels, repeated, with the first and last stop the
   * same colour, and a travel of exactly one tile: the frame after the last
   * is identical to the first, whatever the name is. The keyframes live in
   * the stylesheet rather than in a <style> tag inside every name.
   */
  const style: CSSProperties = {
    backgroundImage: `linear-gradient(100deg, ${a} 0%, ${b} 50%, ${a} 100%)`,
    backgroundSize: 'var(--uname-tile, 220px) 100%',
    backgroundRepeat: 'repeat-x',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    ...(faceStyle || {})
  };

  return (
    <span
      {...keys}
      style={style}
      onClick={handle}
      role={handle ? 'button' : undefined}
      className={cn(
        'min-w-0 truncate py-[.14em] -my-[.14em] font-bold',
        handle && 'cursor-pointer rounded-[4px] outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        big && 'text-[22px] font-extrabold',
        'uname',
        user.nameAnimated && 'uname-animated',
        className
      )}
    >
      {name}
    </span>
  );
}

function lighten(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m || !m[1]) return hex;
  const n = parseInt(m[1], 16);
  const mix = (c: number) => Math.round(c + (255 - c) * 0.45);
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => mix(c).toString(16).padStart(2, '0')).join('')}`;
}

export function TierTag({ tier }: { tier: PublicUser['tier'] }) {
  if (tier === 'vip') return <Tag kind="gold">VIP</Tag>;
  if (tier === 'premium') return <Tag kind="accent">PREMIUM</Tag>;
  return null;
}

export function RoleTag({ role }: { role: PublicUser['role'] }) {
  if (role === 'admin') return <Tag kind="bad">ADMIN</Tag>;
  if (role === 'mod') return <Tag kind="info">MOD</Tag>;
  return null;
}

/* ------------------------------------------------------------ badges */

/**
 * Badges, five at a time then a "+N" into the full list. They sit in a row
 * and never wrap - a tray that stacks is a tray that has been given a column
 * layout by something else.
 */
export function BadgeTray({
  user, max = 5, size = 'md'
}: { user: PublicUser; max?: number; size?: 'sm' | 'md' | 'lg' }) {
  const catalog = useSession((s) => s.catalog);
  const [all, setAll] = useState(false);
  // Where the "+N" button is on screen, so the full list can be drawn in a
  // portal. It used to be an absolutely positioned child, which the profile
  // header - it has overflow:hidden to clip the banner - simply cut off, so
  // pressing "+N" looked like it did nothing at all.
  const plusRef = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const owned = user.badges ?? [];
  if (!owned.length) return null;

  const box = size === 'sm' ? 22 : size === 'lg' ? 34 : 26;
  const shown = owned.slice(0, max);
  const hidden = owned.length - shown.length;
  const find = (id: string) => catalog?.badges.find((b) => b.id === id);

  return (
    <span className="relative flex min-w-0 flex-none flex-row flex-nowrap items-center gap-1" data-keeps-card>
      {shown.map((id) => {
        const badge = find(id);
        return (
          <Tip key={id} text={badge?.name ?? id} sub={badge?.desc}>
            <span className="transition-transform duration-200 hover:-translate-y-0.5">
              <BadgeMark badge={badge} size={box} />
            </span>
          </Tip>
        );
      })}

      {hidden > 0 ? (
        <Tip text={'Show all ' + owned.length + ' badges'}>
          <button
            type="button"
            ref={plusRef}
            onClick={(e) => {
              e.stopPropagation();
              const box = plusRef.current?.getBoundingClientRect();
              if (box) setAt({ top: box.bottom + 8, right: window.innerWidth - box.right });
              setAll(!all);
            }}
            // Sized off the same box as the marks and set in tabular figures,
            // so +9 and +13 occupy the same width and the row never shifts.
            style={{ width: box, height: box, fontSize: Math.round(box * 0.4) }}
            className="grid flex-none place-items-center rounded-[30%] border border-line bg-surface-3 font-bold text-mute tabular-nums transition-colors hover:border-accent-line hover:bg-surface-4 hover:text-ink"
          >
            +{hidden}
          </button>
        </Tip>
      ) : null}

      {all && at ? createPortal(
        <>
          <span className="fixed inset-0 z-[430]" onClick={(e) => { e.stopPropagation(); setAll(false); }} />
          <span
            data-keeps-card
            onClick={(e) => e.stopPropagation()}
            style={{ top: at.top, right: at.right }}
            className="fixed z-[440] flex max-h-[320px] w-[288px] flex-col overflow-hidden rounded-[var(--radius)] border border-line bg-surface-3 shadow-[var(--shadow-3)] animate-[rise_160ms_var(--ease-out-soft)]"
          >
            <span className="flex flex-none items-center gap-2 border-b border-line-soft px-3 py-2">
              <span className="flex-1 text-[12px] font-bold">Badges</span>
              <span className="text-[11.5px] text-mute">{owned.length}</span>
            </span>
            <span className="min-h-0 flex-1 overflow-y-auto p-1.5">
              {TIER_ORDER.map((tier) => {
                const inTier = owned.filter((id) => find(id)?.tier === tier);
                if (!inTier.length) return null;
                return (
                  <span key={tier} className="block">
                    <span className="block px-2 pt-1.5 pb-1 text-[9.5px] font-bold tracking-[.09em] text-ghost uppercase">
                      {tier}
                    </span>
                    {inTier.map((id) => {
                      const badge = find(id);
                      return (
                        <span key={id} className="flex items-start gap-2.5 rounded-[calc(8px*var(--radius-mul,1))] px-2 py-1.5 hover:bg-surface-4">
                          <BadgeMark badge={badge} size={26} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[12.5px] font-bold">{badge?.name ?? id}</span>
                            <span className="block text-[11.5px] leading-snug text-mute">{badge?.desc}</span>
                          </span>
                        </span>
                      );
                    })}
                  </span>
                );
              })}
            </span>
          </span>
        </>,
        document.body
      ) : null}
    </span>
  );
}

const TIER_ORDER = ['mythic', 'epic', 'rare', 'common'] as const;

/* --------------------------------------------------------- user card */

type CardState = { user: PublicUser; anchor: DOMRect; owner: HTMLElement } | null;

const listeners = new Set<(next: CardState) => void>();
let current: CardState = null;

/** Opens the popout. A second click on the same trigger closes it. */
export function openUserCard(user: PublicUser, anchor: HTMLElement): void {
  /* Already looking at this person - inside their own card or their full
     profile - so pressing their picture or name there does nothing, rather
     than opening the same card again on top. */
  if (isShowing(user.username, anchor)) return;
  if (current && current.owner === anchor) { setCard(null); return; }
  setCard({ user, anchor: anchor.getBoundingClientRect(), owner: anchor });
}

/** True when the element sits inside that player's own card or full profile. */
export function isShowing(username: string, el: Element): boolean {
  const host = el.closest('[data-profile-of]');
  return !!host && host.getAttribute('data-profile-of')?.toLowerCase() === username.toLowerCase();
}

function setCard(next: CardState, restoreFocus = false): void {
  const prev = current;
  current = next;
  for (const fn of listeners) fn(next);
  /* Back to whatever opened it when it was closed on purpose - Escape or the
     close button - so a keyboard user is not dropped at the top of the page. */
  if (!next && restoreFocus && prev?.owner && document.contains(prev.owner)) {
    prev.owner.focus({ preventScroll: true });
  }
}

/** Shuts the small profile card, from anywhere. */
export function closeUserCard(): void { setCard(null); }

export function UserCardHost() {
  const [state, setState] = useState<CardState>(current);
  const ref = useRef<HTMLDivElement>(null);
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const queryClient = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    listeners.add(setState);
    return () => { listeners.delete(setState); };
  }, []);

  /*
   * On the shared stack rather than listening for itself.
   *
   * The trigger is kept because pressing the same person twice is a toggle:
   * its own click handler does the closing, and shutting the card here first
   * would let that handler open it straight back up.
   */
  useLayer(!!state, {
    node: () => ref.current,
    keep: (t) => !!state?.owner.contains(t),
    close: () => setCard(null)
  });

  useEffect(() => {
    if (!state) return;
    /* Escape restores focus to whatever opened it, which the stack does not
       know about - so this one is still the card's own. */
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setCard(null, true); }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [state]);

  const { data, isPending: loading, isError: failed, refetch } = useQuery({
    queryKey: ['user', state?.user.username],
    queryFn: () => endpoints.user(state!.user.username),
    enabled: !!state
  });

  /*
   * Measured, not assumed. The placement used to reserve a guessed 460px;
   * a longer card ran off the bottom and a shorter one sat oddly high. The
   * card is drawn, measured, and placed again within the window's margins.
   */
  const [height, setHeight] = useState(460);
  const [, reflow] = useState(0);
  useLayoutEffect(() => {
    if (!state || !ref.current) return;
    const h = ref.current.offsetHeight;
    if (h && Math.abs(h - height) > 1) setHeight(h);
  });
  useEffect(() => {
    if (!state) return;
    const onResize = () => reflow((n) => n + 1);
    window.addEventListener('resize', onResize);
    ref.current?.focus({ preventScroll: true });
    return () => window.removeEventListener('resize', onResize);
  }, [state]);

  if (!state) return null;
  const user = data?.user ?? state.user;
  const relation = data?.relation ?? null;
  const isMe = me?.id === user.id;

  const width = 348;
  const margin = 12;
  const narrow = window.innerWidth < 640;
  /* Where the trigger is now, not where it was when clicked - the page may
     have scrolled or resized since. */
  const a = state.owner.isConnected ? state.owner.getBoundingClientRect() : state.anchor;
  let left = a.right + 10;
  if (left + width > window.innerWidth - margin) left = a.left - width - 10;
  left = Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - width - margin));
  const usable = window.innerHeight - margin * 2;
  const top = Math.min(Math.max(margin, a.top - 10), Math.max(margin, window.innerHeight - Math.min(height, usable) - margin));

  const banner = user.banner ? absolute(user.banner) : null;

  const roleLabel = user.role === 'admin' ? 'Admin' : user.role === 'mod' ? 'Moderator' : 'Member';
  const isUp = user.status !== 'offline' && user.status !== 'invisible';
  /* What they are playing right now, when the desktop app can see it; their
     first saved game otherwise. */
  const playingNow = !!user.playing;
  const topGame = (user.playing ? (user.games?.find((g) => g.id === user.playing) ?? { id: user.playing }) : null) ?? user.games?.[0] ?? null;
  const topGameMeta = catalog?.games.find((g) => g.id === topGame?.id);
  const mutuals = data?.mutuals ?? [];
  const activeParty = data?.parties?.[0] ?? null;

  async function relate(path: string, body: Record<string, unknown>, note: string) {
    try {
      await api.post(path, body);
      toast(note, 'good');
      await queryClient.invalidateQueries({ queryKey: ['friends'] });
      await queryClient.invalidateQueries({ queryKey: ['user', user.username] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }
  const onAddFriend = () => void relate('/api/friends/request', { username: user.username }, 'Friend request sent');
  const onUnfriend = () => void relate('/api/friends/remove', { username: user.username }, 'Friend removed');
  const onInvite = () => { setCard(null); go('parties'); };

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={(user.displayName || user.username) + "'s profile"}
      data-profile-of={user.username}
      tabIndex={-1}
      style={{
        /* A bottom sheet on a phone; anchored beside the trigger otherwise. */
        ...(narrow
          ? { left: 0, right: 0, bottom: 0, width: '100%', maxHeight: '88dvh' }
          : { left, top, width, maxHeight: 'calc(100vh - 24px)' }),
        ['--profile-accent' as string]: user.accent ?? 'var(--accent)',
        ['--banner-color' as string]: user.bannerColor ?? 'var(--bg-3)'
      }}
      className={cn(
        'fixed z-[440] flex flex-col overflow-hidden border border-line-soft bg-surface-1 shadow-[var(--shadow-3)] outline-none animate-[cardin_220ms_var(--ease-out-soft)]',
        narrow ? 'rounded-t-[calc(20px*var(--radius-mul,1))]' : 'rounded-[calc(20px*var(--radius-mul,1))]'
      )}
    >
      {/*
        The player's frame and profile effect, on a layer of their own.

        They used to be attributes on this element, and the stylesheet gives
        [data-pframe] position: relative. That rule was unlayered, so it beat
        the layered Tailwind `fixed` class - for anyone wearing a frame the
        card stopped being fixed and dropped to the bottom of the page, about
        1,370px down and off screen. That was the Crew bug: those members wear
        frames, the friend tested in Friends did not. The rule is layered now
        as well, but on its own layer the frame cannot touch the card's
        positioning whatever the stylesheet says.
      */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[5] rounded-[inherit]"
      />

      {/* ---- the banner ---- */}
      <div className="relative flex-none">
        <div
          className="relative h-[132px] overflow-hidden"
          style={{
            background: banner
              ? 'var(--bg-0)'
              : 'linear-gradient(120deg, color-mix(in srgb, var(--profile-accent) 42%, var(--banner-color)), var(--banner-color))'
          }}
        >
          {banner ? <img src={banner} alt="" className="h-full w-full object-cover" /> : null}
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface-1/95 via-surface-1/25 to-transparent" />
        </div>

        <button
          type="button"
          aria-label="Close"
          onClick={() => setCard(null, true)}
          className="absolute top-2.5 right-2.5 grid h-8 w-8 place-items-center rounded-full bg-[rgba(0,0,0,.5)] text-white/80 backdrop-blur-sm transition-colors hover:bg-[rgba(0,0,0,.7)] hover:text-white"
        >
          <Icons.Close size={15} />
        </button>
      </div>

      {/* The identity block sits outside the scroller: the picture hangs above
          the banner's bottom edge, and a scroll container would clip anything
          above its own top. */}
      <div className="relative z-[1] -mt-[46px] flex flex-none items-start gap-3.5 px-4 pb-3">
        <span className="relative flex-none">
          <FramedAvatar user={user} size={92} ring="var(--bg-1)" frameless presence />
        </span>
        <div className="min-w-0 flex-1 pt-[50px]">
          <div className="flex items-center gap-2">
            <UserName user={user} profile={false} className="truncate text-[22px] leading-tight font-extrabold" />
            <CrewTag crew={user.crew} size="md" />
            {user.badges?.includes('verified') ? (
              <Icons.CheckCircle size={17} className="flex-none text-accent-hi" />
            ) : null}
            <span className="flex-1" />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px]">
            <span className="text-mute">@{user.username}</span>
            <span className="flex items-center gap-1.5 text-dim">
              <i aria-hidden className={cn('h-2 w-2 rounded-full', user.status === 'dnd' ? 'bg-bad' : user.status === 'idle' ? 'bg-warn' : isUp ? 'bg-good' : 'bg-ghost')} />
              {user.status === 'dnd' ? 'Do not disturb' : user.status === 'idle' ? 'Away' : isUp ? 'Online' : 'Offline'}
            </span>
            <TierTag tier={user.tier} />
            {user.role !== 'user' ? (
              <span className="flex items-center gap-1 font-semibold text-accent">
                {user.role === 'admin' ? <Icon name="crown" size={13} /> : null}
                {roleLabel}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* What is still arriving, or what could not. The card opens straight
          away with what the list already knew; the rest fills in. */}
      {loading ? (
        <p role="status" className="mx-4 mb-3 flex items-center gap-2 text-[13px] text-mute">
          <span aria-hidden className="h-3 w-3 animate-spin rounded-full border-2 border-line border-t-accent motion-reduce:animate-none" />
          Loading profile…
        </p>
      ) : failed ? (
        <p role="alert" className="mx-4 mb-3 flex flex-wrap items-center gap-2 rounded-[var(--radius-l)] bg-surface-2 px-3 py-2 text-[13px] text-dim">
          Some of this profile could not load.
          <button type="button" onClick={() => void refetch()} className="font-semibold text-accent hover:underline">Try again</button>
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-1">
        {user.bio ? (
          <p className="selectable mb-3 text-[13px] leading-relaxed text-dim">
            <EmojiText text={user.bio} size={16} />
          </p>
        ) : null}



        {/* Level, and nothing else.

            "Trusted Host" and "Reputation" were labels with no system behind
            them - Pugo has no reputation score, so a card claiming one was
            inventing a fact about a person. Level is counted from real XP. */}
        {/*
          Level, as a bar with the number on it.

          The old row was a star, the word "Level", the number, and a mic -
          four things saying one thing. This is the number, how far through
          it they are, and the one fact that decides whether you invite them
          to a voice party.
        */}
        <div className="mb-2 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5">
          <div className="flex items-baseline gap-2">
            <span className="text-[13px] font-extrabold tracking-[.02em] text-ink">LEVEL {user.level}</span>
            <span className="flex-1" />
            <span className="text-[11.5px] text-mute tabular-nums">
              {user.levelInto.toLocaleString()} / {user.levelNeed.toLocaleString()} XP
            </span>
          </div>
          <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-surface-4">
            <span
              className="block h-full rounded-full bg-[linear-gradient(90deg,var(--accent),var(--accent-hi))]"
              style={{ width: (user.levelNeed ? Math.round((user.levelInto / user.levelNeed) * 100) : 0) + '%' }}
            />
          </span>
          <div className="mt-1.5 flex items-center gap-2 text-[11.5px]">
            <span className="text-mute">{Math.max(0, user.levelNeed - user.levelInto).toLocaleString()} XP to level {user.level + 1}</span>
            <span className="flex-1" />
            <span className={cn('flex items-center gap-1 font-semibold', user.mic ? 'text-good' : 'text-ghost')}>
              <Icon name={user.mic ? 'mic' : 'micOff'} size={12} />
              {user.mic ? 'Has a mic' : 'No mic'}
            </span>
          </div>
        </div>

        {/* what they play */}
        {topGame ? (
          <div className="mb-2">
            <button
              type="button"
              onClick={() => { setCard(null); go('parties?game=' + encodeURIComponent(topGame.id)); }}
              className="flex w-full items-center gap-2.5 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-2.5 text-left outline-none transition-colors hover:bg-surface-3"
            >
              <span
                className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] border"
                style={{
                  borderColor: 'color-mix(in srgb, ' + (topGameMeta?.color ?? 'var(--accent)') + ' 40%, transparent)',
                  background: 'color-mix(in srgb, var(--bg-0) 65%, transparent)',
                  color: topGameMeta?.color ?? 'var(--accent)'
                }}
              >
                {gameIconArt(topGame.id)
                  ? <img src={gameIconArt(topGame.id)!} alt="" className="h-full w-full object-cover" />
                  : <Icon name={topGameMeta?.icon ?? 'dice'} size={19} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-bold text-ink">{topGameMeta?.name ?? topGame.id}</span>
                <span className="block truncate text-[12px] text-mute">
                  {playingNow ? 'Playing now' : (topGame.rank || 'Unranked')}
                </span>
              </span>
              <Icon name="chevronR" size={16} className="flex-none text-ghost" />
            </button>
          </div>
        ) : null}

        {/* where they are right now */}
        {activeParty ? (
          <Block label="Active party">
            <button
              type="button"
              onClick={() => { setCard(null); go('party/' + activeParty.id); }}
              className="flex w-full items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3 text-left transition-colors hover:bg-surface-3"
            >
              <div className="flex flex-none items-center">
                {activeParty.members.slice(0, 3).map((m, i) => m.user ? (
                  <span key={m.userId} className="rounded-[30%] ring-2 ring-surface-2" style={{ marginLeft: i ? -8 : 0 }}>
                    <Avatar user={m.user} size={28} />
                  </span>
                ) : null)}
                {activeParty.memberCount > 3 ? (
                  <span
                    className="grid h-[28px] min-w-[28px] place-items-center rounded-[30%] bg-surface-4 px-1 text-[10.5px] font-bold text-dim ring-2 ring-surface-2"
                    style={{ marginLeft: -8 }}
                  >
                    +{activeParty.memberCount - 3}
                  </span>
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-bold">{activeParty.title}</div>
                <div className="truncate text-[12px] text-mute tabular-nums">
                  {activeParty.memberCount} / {activeParty.size} members
                </div>
              </div>
              <Icon name="chevronR" size={15} className="flex-none text-ghost" />
            </button>
          </Block>
        ) : null}

        {/* who you both know */}
        {mutuals.length ? (
          <button
            type="button"
            onClick={() => { setCard(null); openProfile(user.username); }}
            className="mb-2 flex w-full items-center gap-2.5 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-2.5 text-left transition-colors hover:bg-surface-3"
          >
            <span className="flex flex-none items-center">
              {mutuals.slice(0, 3).map((m, i) => (
                <span key={m.id} className="rounded-[30%] ring-2 ring-surface-2" style={{ marginLeft: i ? -8 : 0 }}>
                  <Avatar user={m} size={28} />
                </span>
              ))}
            </span>
            <span className="min-w-0 flex-1 text-[13.5px] font-semibold text-ink">
              {mutuals.length} {mutuals.length === 1 ? 'mutual friend' : 'mutual friends'}
            </span>
            <Icon name="chevronR" size={16} className="flex-none text-ghost" />
          </button>
        ) : null}
      </div>

      {/* the actions: the one you came for, full width, then the rest */}
      <div className="flex-none px-4 pt-1 pb-3">
        {isMe ? (
          <>
            <ProfileAction primary wide icon="edit" onClick={() => { setCard(null); go('settings/profile'); }}>
              Edit profile
            </ProfileAction>
            <div className="mt-2">
              <ProfileAction wide icon="arrowR" onClick={() => { setCard(null); openProfile(user.username); }}>
                View full profile
              </ProfileAction>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <ProfileAction primary icon="chat" onClick={() => { setCard(null); go('chat/' + user.username); }}>
                Message
              </ProfileAction>
              <ProfileAction icon="addFriend" onClick={onInvite}>Invite to party</ProfileAction>
              {relation?.status === 'accepted' ? (
                <ProfileAction icon="users" onClick={onUnfriend}>Remove friend</ProfileAction>
              ) : relation ? null : (
                <ProfileAction icon="addFriend" onClick={onAddFriend}>Add friend</ProfileAction>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setCard(null); openProfile(user.username); }}
              className="mt-2.5 flex w-full items-center justify-center gap-1.5 border-t border-line-soft pt-2.5 text-[13px] font-semibold text-accent outline-none transition-colors hover:text-[var(--accent-hi)]"
            >
              View full profile
              <Icon name="arrowR" size={15} />
            </button>
          </>
        )}
      </div>

      {/* the footer: when they arrived, and the name you copy */}
      <div className="flex flex-none items-center gap-2 border-t border-line-soft px-4 py-2.5">
        <Icon name="calendar" size={14} className="text-ghost" />
        <span className="text-[11.5px] text-mute">
          Member since {new Date(user.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
        <span className="flex-1" />
        <span className="selectable font-mono text-[11.5px] text-mute">{user.username}</span>
        <button
          type="button"
          aria-label="Copy username"
          onClick={() => { void copyText(user.username); }}
          className="grid h-6 w-6 place-items-center rounded-[calc(6px*var(--radius-mul,1))] text-ghost transition-colors hover:bg-surface-3 hover:text-ink"
        >
          <Icon name="copy" size={13} />
        </button>
      </div>
    </div>,
    document.body
  );
}

/** One of the three facts in the strip under the name. */

/** A labelled block in the popout. */
function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="px-4 pb-4">
      <h3 className="mb-2 text-[13px] font-semibold text-mute">{label}</h3>
      {children}
    </section>
  );
}

/** One of the buttons at the bottom of the popout. */
function ProfileAction({ icon, primary, wide, onClick, children }: {
  icon: string;
  primary?: boolean;
  /** The one action you came for, across the card. */
  wide?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] text-[13px] font-bold',
        'transition-[filter,background,border-color] duration-150',
        wide ? 'h-[42px] w-full text-[14px]' : 'h-[38px]',
        primary
          ? 'bg-[linear-gradient(180deg,var(--accent-hi),var(--accent))] text-accent-fg shadow-[inset_0_1px_0_rgba(255,255,255,.18)] hover:bg-accent-hi'
          : 'border border-line bg-surface-2 text-dim hover:border-accent-line hover:text-ink'
      )}
    >
      <Icon name={icon} size={15} />
      {children}
    </button>
  );
}

/**
 * The three dots beside Message.
 *
 * Everything you can do about this person, grouped: who they are, the party,
 * then the things you would rather not need.
 */


export function absolute(url: string): string {
  return /^https?:/i.test(url) ? url : serverUrl() + url;
}

/* ---------------------------------------------------------- the row */

/** A player in a list. Clicking anywhere on it opens the popout. */
export function UserRow({
  user, sub, right, active, onClick, badges, relation
}: {
  user: PublicUser;
  sub?: ReactNode;
  right?: ReactNode;
  active?: boolean;
  badges?: boolean;
  relation?: { status: string; actorId: string; id: string } | null;
  onClick?: () => void;
}) {
  const menu = useUserMenu(user, relation);
  return (
    // A div rather than a button: rows carry their own action buttons on the
    // right, and a button inside a button is invalid HTML that React refuses
    // to render predictably. The role and key handler keep it reachable.
    <div
      role="button"
      tabIndex={0}
      {...menu.props}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick();
        else openUserCard(user, e.currentTarget);
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        if (onClick) onClick();
        else openUserCard(user, e.currentTarget);
      }}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-[calc(10px*var(--radius-mul,1))] px-[calc(var(--row-pad)-1px)] py-[var(--row-pad)] text-left transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent-hi',
        active ? 'bg-surface-3' : 'hover:bg-surface-2'
      )}
    >
      <Avatar user={user} size={36} presence />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <UserName user={user} className="text-[14px] font-semibold" />
          {badges ? <BadgeTray user={user} max={3} size="sm" /> : null}
        </span>
        {sub ? <span className="block truncate text-[12.5px] text-mute">{sub}</span> : null}
      </span>
      {right}
      {menu.menu}
    </div>
  );
}

